import React, { forwardRef } from 'react';
import SlideEngine from './Slides/SlideEngine.comp';
import { getEffectiveLayout } from './Slides/slideRegistry';
import { normalizeUiLocale, tUi } from '../../../../../../utils/dq_lc';
import { DECK_WIDTH } from '../../../utils/LearnTab/slideDeck.utils';

// Every session's slides on one page for the HTML download: a table of contents, a cover per session, then each
// slide at the preview's desktop width. Slides grow with their content (nothing is cut off); intro videos play.
const DECK_CSS = `
.slide-deck { width: ${DECK_WIDTH}px; margin: 0 auto; padding: 24px 0 48px; font-family: 'Source Sans Pro', 'Noto Sans', sans-serif; }
.slide-deck .deck-toc, .slide-deck .deck-cover { background: #fff; padding: 40px 96px; margin-bottom: 16px; color: #052F44; }
.slide-deck .deck-toc h1, .slide-deck .deck-cover h2 { margin: 0 0 12px; }
.slide-deck .deck-toc p, .slide-deck .deck-cover p { margin: 4px 0; color: #4a5a66; }
.slide-deck .deck-toc ol { margin: 16px 0 0; padding-left: 24px; line-height: 1.8; }
.slide-deck .deck-toc a { color: #052F44; }
.slide-deck .deck-page { position: relative; min-height: 720px; padding-bottom: 32px; margin-bottom: 16px; background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12); }
.slide-deck .deck-video { padding: 40px 96px; color: #052F44; }
.slide-deck .deck-video video { width: 100%; max-height: 620px; background: #000; }
.slide-deck .deck-footer { position: absolute; right: 24px; bottom: 10px; font-size: 12px; color: #8a96a0; }
.slide-deck .answer-label { display: none !important; }
.slide-deck [data-lab-copy] { display: none !important; }
.slide-deck pre[data-lab-artifact] { white-space: pre-wrap; word-break: break-word; }
`;

const videoSourceOf = (slide, session) => slide.VideoUrl || slide.IntroVideo || session.videoUrl || null;

const SlideDeckDoc = forwardRef(({ programTitle = '', courseCode = '', sessions = [] }, ref) => {
    return (
        <div ref={ref} className="slide-deck">
            <style>{DECK_CSS}</style>
            <nav className="deck-toc">
                <h1>{programTitle}</h1>
                <p>{courseCode}</p>
                <ol>
                    {sessions.map(session => (
                        <li key={session.session_code}>
                            <a href={`#deck-${session.session_code}`}>{session.session_code} · {session.session_title}</a>
                        </li>
                    ))}
                </ol>
            </nav>
            {sessions.map((session) => {
                const uiLocale = normalizeUiLocale(session.language);
                const total = session.slides.length;
                return (
                    <section key={session.session_code} id={`deck-${session.session_code}`} data-deck-session={session.session_code}>
                        <div className="deck-cover">
                            <h2>{session.session_title}</h2>
                            <p>{[session.module_title, session.unit_title].filter(Boolean).join(' · ')}</p>
                            <p>{session.session_code} · {total ? tUi('preview.deck.slideCount', uiLocale, { count: total }) : tUi('preview.deck.noSlides', uiLocale)}</p>
                        </div>
                        {session.slides.map((slide, i) => (
                            <div className="deck-page" key={`${session.session_code}-${i}`} data-deck-slide={`${session.session_code}-${i + 1}`}>
                                {getEffectiveLayout(slide) === 'VideoTemplate' ? (
                                    <div className="deck-video">
                                        <h2>{slide.SlideTitle || ''}</h2>
                                        {videoSourceOf(slide, session)
                                            ? <video controls preload="metadata" src={videoSourceOf(slide, session)} />
                                            : <p>{tUi('preview.deck.noVideo', uiLocale)}</p>}
                                    </div>
                                ) : (
                                    <SlideEngine
                                        slide={slide}
                                        videoUrl={session.videoUrl}
                                        langCode={session.language}
                                        uiLocale={uiLocale}
                                        isEditMode={false}
                                        isPollCorrect
                                    />
                                )}
                                <span className="deck-footer">{session.session_code} · {i + 1} / {total}</span>
                            </div>
                        ))}
                    </section>
                );
            })}
        </div>
    );
});

export default SlideDeckDoc;
